<script setup>
import { ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import AppLayout from '@/Layouts/AppLayout.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'

const props = defineProps({
    tenants: Object,
    protectedSlugs: {
        type: Array,
        default: () => [],
    },
})

const isProtected = (tenant) => props.protectedSlugs.includes(tenant.slug)

const toggleStatus = (tenant) => {
    if (isProtected(tenant)) return

    const routeName =
        tenant.status === 'ACTIVE'
            ? 'admin.tenant-manager.deactivate'
            : 'admin.tenant-manager.activate'

    Inertia.patch(route(routeName, tenant.id), {}, { preserveScroll: true })
}
</script>

<template>
    <AppLayout title="Tenants">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">Tenant Manager</h2>
        </template>


        <div class="mx-auto py-10 sm:px-6 lg:px-8">
            <div class="mb-4 flex justify-end items-center">
                <a :href="route('admin.tenant-manager.create')">
                    <PrimaryButton>Add Tenant</PrimaryButton>
                </a>
            </div>

            <table class="min-w-full w-full text-left text-sm">
                <thead class="border-b">
                    <tr>
                        <th class="px-4 py-2">Name</th>
                        <th class="px-4 py-2">App Name</th>
                        <th class="px-4 py-2">Identifier</th>
                        <th class="px-4 py-2">Status</th>
                        <th class="px-4 py-2">Logo</th>
                        <th class="px-4 py-2">Primary Domain</th>
                        <th class="px-4 py-2">Testing Domain</th>
                        <th class="px-4 py-2">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="t in tenants.data" :key="t.id" class="border-b">
                        <td class="px-4 py-2">{{ t.tenantName }}</td>

                        <td class="px-4 py-2">{{ t.app_name || t.tenantName }}</td>

                        <td class="px-4 py-2">{{ t.slug }}</td>

                        <td class="px-4 py-2">
                            <span :class="t.status === 'ACTIVE' ? 'text-green-700' : 'text-gray-500'">
                                {{ t.status === 'ACTIVE' ? 'Enabled' : 'Disabled' }}
                            </span>
                        </td>

                        <td class="px-4 py-2">
                            <img v-if="t.logo_url" :src="t.logo_url" alt="Logo"
                                class="h-10 w-10 object-contain rounded" />
                            <span v-else class="text-gray-400 italic">No logo</span>
                        </td>

                        <td class="px-4 py-2">
                            <span v-if="t.primary_domain">{{ t.primary_domain }}</span>
                            <span v-else class="text-gray-400 italic">No primary domain</span>
                        </td>

                        <td class="px-4 py-2">
                            <span v-if="t.testing_domain">{{ t.testing_domain }}</span>
                            <span v-else class="text-gray-400 italic">No testing domain</span>
                        </td>

                        <td class="px-4 py-2 space-x-3">
                            <template v-if="!isProtected(t)">
                                <button @click="toggleStatus(t)" :class="[
                                    'fa cursor-pointer transition-colors',
                                    t.status === 'ACTIVE'
                                        ? 'fa-toggle-on text-green-600 hover:text-green-800'
                                        : 'fa-toggle-off text-gray-500 hover:text-gray-700'
                                ]" :title="t.status === 'ACTIVE' ? 'Disable' : 'Enable'" />
                                <a :href="route('admin.tenant-manager.edit', t.id)" class="fa fa-pen" title="Edit" />
                                <a :href="route('admin.tenant-manager.navigation.edit', t.id)" class="fa fa-sliders-h" title="Navigation" />
                                <a :href="route('admin.tenant-manager.buttons.edit', t.id)" class="fa fa-th-large" title="Buttons" />
                            </template>

                            <template v-else>
                                <span class="text-gray-400 text-xs uppercase tracking-wide">Locked</span>
                            </template>
                        </td>
                    </tr>

                    <tr v-if="tenants.data.length === 0">
                        <td colspan="3" class="px-4 py-8 text-center text-gray-500">
                            No tenants found.
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="mt-4">
                <PaginationLinks :pagination="tenants" />
            </div>
        </div>
    </AppLayout>
</template>
